import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import {
  subscribeNotifications,
  markAllNotificationsRead,
  migrateLocalStorageNotifications,
  countAccountLevelUnread,
  notificationThreadLink,
  FirestoreNotification,
} from '../repositories/notificationsRepository';
import { openThreadFromNotification } from '../lib/threadDeepLink';
import NotificationPermissionCard from './common/NotificationPermissionCard';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: 0.5rem 0.75rem;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
`;

const HeaderButton = styled.button<{ $open: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  background: transparent;
  border: none;
  color: #333;
  padding: 0.5rem;
  border-radius: 8px;
  font-size: 0.95rem;
  font-weight: 600;
  text-transform: lowercase;
  cursor: pointer;
  transition: background 0.2s ease;

  &:hover {
    background: rgba(0, 0, 0, 0.03);
  }
`;

const HeaderLeft = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.6rem;
  color: #333;

  svg {
    width: 18px;
    height: 18px;
    color: #333;
  }
`;

const Badge = styled.span`
  min-width: 20px;
  height: 20px;
  padding: 0 6px;
  border-radius: 10px;
  background: var(--brand-danger);
  color: white;
  font-size: 0.75rem;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;

const Caret = styled.span<{ $open: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.2s ease;
  transform: rotate(${props => props.$open ? 180 : 0}deg);

  svg {
    width: 16px;
    height: 16px;
    color: #666;
  }
`;

const Panel = styled.div<{ $open: boolean }>`
  margin-top: 0.25rem;
  background: white;
  border-radius: 10px;
  border: 1px solid rgba(0,0,0,0.06);
  box-shadow: 0 8px 20px rgba(0,0,0,0.05);
  overflow: hidden;
  /* Room for the device card above the list (2026-09-08): 360px clipped the
     list's "view all" once the card sat on top of it. */
  max-height: ${props => props.$open ? '560px' : '0'};
  opacity: ${props => props.$open ? 1 : 0};
  transform: translateY(${props => props.$open ? '0' : '-4px'});
  transition: max-height 0.25s ease, opacity 0.2s ease, transform 0.2s ease;
`;

const List = styled.div`
  max-height: 300px;
  overflow-y: auto;
`;

const Item = styled.div<{ $clickable?: boolean }>`
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 0.25rem 0.75rem;
  padding: 0.75rem 0.75rem;
  border-bottom: 1px solid rgba(0,0,0,0.04);
  cursor: ${props => props.$clickable ? 'pointer' : 'default'};

  &:hover {
    background: ${props => props.$clickable ? 'rgba(0,0,0,0.03)' : 'transparent'};
  }

  &:focus-visible {
    outline: 2px solid var(--brand-gold-hover);
    outline-offset: -2px;
  }

  &:last-child {
    border-bottom: none;
  }

  .title {
    font-weight: 600;
    color: #333;
    font-size: 0.9rem;
  }
  .time {
    color: var(--brand-text-muted);
    font-size: 0.75rem;
  }
  .message {
    grid-column: 1 / -1;
    color: #555;
    font-size: 0.85rem;
    line-height: 1.4;
  }
`;

const Empty = styled.div`
  padding: 1rem;
  text-align: center;
  color: var(--brand-text-muted);
  font-style: italic;
`;

const ViewAll = styled.button`
  width: 100%;
  background: #f7f7f7;
  border: none;
  padding: 0.65rem 0.75rem;
  text-align: center;
  font-weight: 600;
  color: #333;
  text-transform: lowercase;
  cursor: pointer;
  border-top: 1px solid rgba(0,0,0,0.06);

  &:hover {
    background: #f0f0f0;
  }
`;

interface MobileNotificationsProps {
  closeMenu?: () => void;
}

const MobileNotifications: React.FC<MobileNotificationsProps> = ({ closeMenu }) => {
  const { isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  const hasAccount = isAuthenticated && !!(user && user.accountId);
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState<FirestoreNotification[]>([]);
  const [unreadCount, setUnreadCount] = useState<number>(0);

  // One-time migration from localStorage to Firestore
  useEffect(() => {
    if (!hasAccount || !user?.id || !user?.accountId) return;
    migrateLocalStorageNotifications(user.id, user.accountId).catch(() => {});
  }, [hasAccount, user?.id, user?.accountId]);

  // Subscribe to real-time Firestore notifications
  useEffect(() => {
    if (!hasAccount || !user?.id) {
      setNotifications([]);
      setUnreadCount(0);
      return;
    }
    const unsubscribe = subscribeNotifications(
      user.id,
      (notifs) => setNotifications(notifs),
      () => setNotifications([]),
    );
    return unsubscribe;
  }, [hasAccount, user?.id]);

  // Account-level unread count (2026-09-08): thread-scoped rows (routine
  // runs, thread messages) are counted by their own thread's badge instead.
  useEffect(() => {
    if (!hasAccount) return;
    setUnreadCount(countAccountLevelUnread(notifications));
  }, [hasAccount, notifications]);

  // Mark all as read when opening panel
  useEffect(() => {
    if (!isOpen || !hasAccount || !user?.id) return;
    if (unreadCount > 0) {
      markAllNotificationsRead(user.id).catch(() => {});
    }
  }, [isOpen]);

  if (!hasAccount) return null;

  const recent = notifications;

  return (
    <Container>
      <HeaderButton
        onClick={() => setIsOpen(v => !v)}
        aria-label="notifications"
        aria-expanded={isOpen}
        $open={isOpen}
      >
        <HeaderLeft>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M18 8a6 6 0 10-12 0c0 7-3 9-3 9h18s-3-2-3-9"/>
            <path d="M13.73 21a2 2 0 01-3.46 0"/>
          </svg>
          notifications
          {unreadCount > 0 && (
            <Badge>{Math.min(9, unreadCount)}{unreadCount > 9 ? '+' : ''}</Badge>
          )}
        </HeaderLeft>
        <Caret $open={isOpen}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="6 9 12 15 18 9"></polyline>
          </svg>
        </Caret>
      </HeaderButton>
      <Panel $open={isOpen}>
        {/* This device's notification permission (2026-09-08) — same card as
            Settings, compact. */}
        {isOpen && <NotificationPermissionCard compact />}
        <List>
          {recent.length === 0 ? (
            <Empty>no notifications yet</Empty>
          ) : (
            recent.map(n => {
              const link = notificationThreadLink(n);
              const open = link
                ? () => {
                    if (closeMenu) closeMenu();
                    openThreadFromNotification(link);
                  }
                : undefined;
              return (
              <Item
                key={n.id}
                $clickable={!!link}
                data-notification-thread-id={link ? link.threadId : undefined}
                role={link ? 'button' : undefined}
                tabIndex={link ? 0 : undefined}
                aria-label={link ? `open thread for: ${n.title || 'notification'}` : undefined}
                onClick={open}
                onKeyDown={link ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); open?.(); } } : undefined}
              >
                <div className="title">{n.title || 'notification'}</div>
                <div className="time">{(() => {
                  try {
                    const d = new Date(n.timestamp);
                    return `${d.toLocaleDateString()} ${d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
                  } catch {
                    return n.timestamp;
                  }
                })()}</div>
                <div className="message">{n.message}</div>
              </Item>
              );
            })
          )}
        </List>
        <ViewAll onClick={() => { if (closeMenu) closeMenu(); navigate('/notifications'); }}>view all</ViewAll>
      </Panel>
    </Container>
  );
};

export default MobileNotifications;
